import { DestroyRef, Injectable, NgZone, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { LayoutService } from '../layout/service/layout.service';
import { HelpCatalog, HelpState, initialHelpState } from './help.models';
import { contextualArticle, validHelpState } from './help.catalog';
import { HelpAccessService } from './help-access.service';

type PendingHelpContext =
    | { kind: 'menu'; category: string; articleId?: string }
    | { kind: 'route'; url: string };

@Injectable({ providedIn: 'root' })
export class HelpService {
    private router = inject(Router);
    private zone = inject(NgZone);
    readonly layout = inject(LayoutService);
    readonly access = inject(HelpAccessService);
    readonly visible = signal(false);
    readonly compact = signal(false);
    readonly state = signal<HelpState>(initialHelpState());
    readonly catalog = signal<HelpCatalog | null>(null);
    readonly notice = signal('');
    readonly revision = signal(0);
    detached = new URLSearchParams(location.search).get('helpWindow') || '';
    private child: Window | null = null;
    private channel = '';
    private trigger: HTMLElement | null = null;
    private loading?: Promise<void>;
    private context = this.router.url;
    private pendingContext: PendingHelpContext | null = null;
    private pendingNavigation = false;
    focusHeading = true;
    private readonly themeTokens = ['--p-primary-color', '--p-primary-hover-color', '--p-primary-contrast-color', '--p-text-color', '--p-text-muted-color', '--p-content-background', '--p-content-hover-background', '--p-content-border-color'];

    constructor() {
        this.access.denied.pipe(takeUntilDestroyed()).subscribe(() => this.lock());
        const media = window.matchMedia('(max-width: 900px)');
        const onResize = () => this.zone.run(() => this.compact.set(media.matches));
        onResize();
        media.addEventListener('change', onResize);
        inject(DestroyRef).onDestroy(() => media.removeEventListener('change', onResize));
        this.router.events.pipe(filter(e => e instanceof NavigationEnd), takeUntilDestroyed()).subscribe(e => {
            if (!e.urlAfterRedirects.startsWith('/ajuda')) {
                this.context = e.urlAfterRedirects;
                if (this.state().follow) {
                    if (!this.visible() && (!this.child || this.child.closed)) this.pendingContext = { kind: 'route', url: this.context };
                    else {
                        this.pendingContext = null;
                        if (this.visible() && this.catalog()) this.openArticle(contextualArticle(this.catalog()!, this.context));
                    }
                }
                this.send('context', { article: this.catalog() ? contextualArticle(this.catalog()!, this.context) : '' });
            }
        });
        const onMessage = (e: MessageEvent) => this.zone.run(() => this.receive(e));
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'F1' && !e.altKey && !e.ctrlKey && !e.metaKey) {
                e.preventDefault();
                this.zone.run(() => { if (!location.pathname.endsWith('/ajuda')) void this.open(); });
            }
        };
        window.addEventListener('message', onMessage);
        window.addEventListener('keydown', onKey);
        inject(DestroyRef).onDestroy(() => { window.removeEventListener('message', onMessage); window.removeEventListener('keydown', onKey); });
        this.layout.configUpdate$.pipe(takeUntilDestroyed()).subscribe(() => requestAnimationFrame(() => this.sendTheme()));
        effect(() => {
            this.layout.transitionComplete();
            requestAnimationFrame(() => this.sendTheme());
        });
    }

    async load(force = false) {
        if (!force && this.catalog() && this.access.authorized()) return;
        if (!this.loading) this.loading = this.access.content().then(catalog => {
            if (catalog && this.access.authorized()) { this.catalog.set(catalog); this.notice.set(''); }
            else this.lock();
        }).finally(() => this.loading = undefined);
        await this.loading;
    }
    private lock() {
        const active = this.visible() || this.router.url.startsWith('/ajuda');
        const unavailable = this.access.failure() === 'unavailable' && this.access.login.isAutenticado();
        this.visible.set(unavailable && this.visible());
        this.catalog.set(null);
        this.state.set(initialHelpState());
        this.pendingContext = null;
        if (unavailable) {
            this.notice.set('A Central de Ajuda está temporariamente indisponível. Sua sessão foi preservada. Tente novamente em instantes.');
            return;
        }
        this.notice.set('Entre no sistema com uma sessão válida para acessar a Ajuda.');
        if (active) void this.router.navigateByUrl('/auth/login');
    }
    async open() {
        this.trigger = document.activeElement as HTMLElement;
        if (!await this.access.ensure()) {
            this.lock();
            if (this.access.failure() === 'unavailable' && this.access.login.isAutenticado()) this.visible.set(true);
            else await this.router.navigateByUrl('/auth/login');
            return;
        }
        await this.load();
        if (!this.catalog() || !this.access.authorized()) {
            if (this.access.failure() === 'unavailable' && this.access.login.isAutenticado()) this.visible.set(true);
            return;
        }
        if (this.child && !this.child.closed) { this.child.focus(); return; }
        const wasVisible = this.visible();
        const pending = this.pendingContext;
        this.pendingContext = null;
        this.visible.set(true);
        if (!wasVisible && this.state().follow && pending?.kind === 'menu') this.followMenuCategory(pending.category, pending.articleId);
        else if (!wasVisible && this.state().follow && pending?.kind === 'route') this.openArticle(contextualArticle(this.catalog()!, pending.url));
        else if (!wasVisible && this.state().history.length === 1) this.openArticle(contextualArticle(this.catalog()!, this.context));
        this.revision.update(n => n + 1);
    }
    close() { this.visible.set(false); setTimeout(() => this.trigger?.focus()); }
    update(patch: Partial<HelpState>) { this.state.update(s => ({ ...s, ...patch })); this.send('state', this.state()); }
    openArticle(id: string, focusHeading = true) {
        if (!this.access.authorized() || !this.access.login.isAutenticado()) { this.lock(); return; }
        if (id && !this.catalog()?.articles.some(a => a.id === id)) return;
        const s = this.state();
        const history = [...s.history.slice(0, s.cursor + 1), id].slice(-100);
        this.focusHeading = focusHeading;
        this.update({ article: id, history, cursor: history.length - 1 });
        this.revision.update(n => n + 1);
    }
    move(delta: number) {
        const s = this.state(), cursor = s.cursor + delta;
        if (cursor < 0 || cursor >= s.history.length) return;
        this.focusHeading = true;
        this.update({ cursor, article: s.history[cursor] });
        this.revision.update(n => n + 1);
    }
    search(patch: Partial<HelpState>) {
        this.update({ ...patch, results: true, positions: { ...this.state().positions, results: 0 } });
        if (this.state().article) this.openArticle('', false);
        else { this.focusHeading = false; this.revision.update(n => n + 1); }
    }
    followMenuCategory(category: string, articleId?: string) {
        if (!this.state().follow || !this.access.login.isAutenticado()) return;
        if (!this.visible() && (!this.child || this.child.closed)) {
            this.pendingContext = { kind: 'menu', category, articleId };
            return;
        }
        const catalog = this.catalog();
        if (!catalog || !this.access.authorized()) return;
        this.pendingContext = null;
        if (!catalog.categories.some(item => item.id === category)) return;
        if (articleId && catalog.articles.some(article => article.id === articleId && article.category === category)) {
            if (this.state().article === articleId && this.state().category === category && !this.state().results) return;
            this.update({ category, type: '', query: '', results: false });
            this.openArticle(articleId);
        } else {
            this.search({ category, type: '', query: '' });
        }
    }
    home() { this.update({ query: '', category: '', type: '', results: false }); this.openArticle(''); }
    remember(position: number) {
        const s = this.state();
        this.update({ positions: { ...s.positions, [s.article || (s.results ? 'results' : '')]: position } });
    }
    setFollow(value: boolean) { if (!value) this.pendingContext = null; this.update({ follow: value }); }

    detach() {
        if (!this.access.authorized() || !this.access.login.isAutenticado()) { this.lock(); return; }
        if (this.child && !this.child.closed) { this.send('state', this.state()); this.child.focus(); this.close(); return; }
        // getRandomValues também funciona no HTTP da rede de testes; randomUUID exige contexto seguro.
        this.channel = Array.from(crypto.getRandomValues(new Uint8Array(16)), value => value.toString(16).padStart(2, '0')).join('');
        const url = new URL('ajuda', document.baseURI);
        url.searchParams.set('helpWindow', this.channel);
        this.child = window.open(url.href, 'lxharvest-help-' + this.channel, 'popup=yes,width=1000,height=800,resizable=yes,scrollbars=yes');
        if (!this.child) { this.notice.set('O navegador bloqueou a nova janela. Permita pop-ups para este site ou continue pelo painel.'); return; }
        this.close();
    }
    async connect() { await this.load(); if (this.catalog()) this.send('ready'); }
    returnToApp() {
        if (!this.access.login.isAutenticado()) { this.lock(); return; }
        if (!this.access.authorized()) { this.visible.set(false); void this.router.navigateByUrl('/'); return; }
        if (this.detached && window.opener && !window.opener.closed) { this.send('return', this.state()); return; }
        this.detached = '';
        this.visible.set(!this.compact());
        void this.router.navigateByUrl('/');
    }
    async navigate(destination: string) {
        if (!this.catalog()?.articles.some(a => a.destination === destination)) return;
        if (this.detached && window.opener && !window.opener.closed) { this.send('navigate', { destination }); return; }
        if (this.pendingNavigation) return;
        // O aplicativo ainda não possui guard global para formulários não salvos.
        if (!this.router.url.startsWith('/ajuda') && !this.router.url.startsWith('/auth') && this.router.url !== destination && !window.confirm('Abrir outra tela? Salve suas alterações antes de continuar.')) return;
        if (!await this.access.ensure()) { this.lock(); if (this.access.failure() !== 'unavailable') await this.router.navigateByUrl('/auth/login'); return; }
        this.pendingNavigation = true;
        try { await this.router.navigateByUrl(destination); } finally { this.pendingNavigation = false; }
    }
    private send(kind: string, data?: unknown) {
        const target = this.detached ? window.opener : this.child;
        if (!target || target.closed) return;
        try { target.postMessage({ app: 'lxharvest-help', channel: this.detached || this.channel, kind, data }, location.origin); } catch { /* janela encerrada */ }
    }
    private sendTheme() {
        if (this.detached) return;
        if (document.documentElement.classList.contains('app-dark') !== !!this.layout.layoutConfig().darkTheme) return;
        const style = getComputedStyle(document.documentElement);
        this.send('theme', { darkTheme: this.layout.layoutConfig().darkTheme, tokens: Object.fromEntries(this.themeTokens.map(key => [key, style.getPropertyValue(key).trim()])) });
    }
    private receive(event: MessageEvent) {
        if (!this.access.authorized() || !this.access.login.isAutenticado()) return;
        const peer = this.detached ? window.opener : this.child;
        if (!peer || event.source !== peer || event.origin !== location.origin) return;
        const m = event.data;
        if (!m || m.app !== 'lxharvest-help' || m.channel !== (this.detached || this.channel)) return;
        const catalog = this.catalog();
        if (m.kind === 'ready' && !this.detached) { this.send('state', this.state()); this.sendTheme(); }
        if ((m.kind === 'state' || m.kind === 'return') && catalog) {
            const state = validHelpState(m.data, catalog);
            if (!state) return;
            this.state.set(state);
            this.focusHeading = true;
            if (this.detached || m.kind === 'return') this.revision.update(n => n + 1);
            if (m.kind === 'return' && !this.detached) { this.visible.set(true); this.send('returned'); window.focus(); }
        }
        if (m.kind === 'returned' && this.detached) window.close();
        if (m.kind === 'context' && this.detached && this.state().follow && typeof m.data?.article === 'string') this.openArticle(m.data.article);
        if (m.kind === 'navigate' && !this.detached && typeof m.data?.destination === 'string') { window.focus(); void this.navigate(m.data.destination); }
        if (m.kind === 'theme' && this.detached && typeof m.data?.darkTheme === 'boolean') {
            this.layout.layoutConfig.update(c => ({ ...c, darkTheme: m.data.darkTheme }));
            document.documentElement.classList.toggle('app-dark', m.data.darkTheme);
            for (const key of this.themeTokens) {
                const value = m.data.tokens?.[key];
                if (typeof value === 'string' && value.length < 150 && CSS.supports('color', value)) document.documentElement.style.setProperty(key, value);
            }
        }
    }
}
